import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FactOnExpert, PossibleEnrichments, Qualification } from '@outdoorly/react-chat-sdk';
import dayjs from 'dayjs';
import { AnimatePresence, useAnimation, Variants } from 'framer-motion';
import { DeepTypeOfArray } from 'pullstate/dist/useStoreStateOpt-types';
import React, { useRef, useState } from 'react';
import {
  FacebookLogo,
  InstagramLogo,
  LeftChevron,
  LinkedinLogo,
  PinterestLogo,
  RedditLogo,
  SpotifyLogo,
  TikTokLogo,
  TumblrLogo,
  TwitterLogo,
  VimeoLogo,
  YouTubeLogo
} from '../../icons';
import { Store } from '../../store';
import { MotionBox } from '../Motion';
import { SkeletonImage } from '../SkeletonImage';
import { QualificationFactPill } from './QualificationFactPill';
import { SocialNetwork as SocialMediaNetworkName } from '@outdoorly/react-chat-sdk';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import posthog from 'posthog-js';

type ExpertProfileProps = {
  expertProfile: NonNullable<PossibleEnrichments['expert']>['expertProfile'];
};
const MAX_SOCIAL_MEDIA_LINKS = 4;
const SOCIAL_MEDIA_ICONS = {
  [SocialMediaNetworkName.Facebook]: (
    <FacebookLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.Instagram]: (
    <InstagramLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.LinkedIn]: (
    <LinkedinLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  // [SocialMediaNetworkName.personal]: (
  //   <FontAwesomeIcon icon={faGlobe} color="scheme.contentInverseSecondary" width="16px" height="16px" />
  // ),
  [SocialMediaNetworkName.Pinterest]: (
    <PinterestLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.Reddit]: (
    <RedditLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Spotify]: (
    <SpotifyLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.TikTok]: (
    <TikTokLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Tumblr]: (
    <TumblrLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Twitter]: (
    <TwitterLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Vimeo]: (
    <VimeoLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.YouTube]: (
    <YouTubeLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  )
};

export const ExpertProfile: React.FC<ExpertProfileProps> = (props) => {
  const { expertProfile } = props;
  const [fullscreen, setFullscreen] = useState(false);
  const expertProfileRef = useRef<null | HTMLDivElement>(null);
  const labels = Store.useState((s) => s.labels);
  const sortedQualifications =
    expertProfile?.qualifications.sort(
      (a, b) => dayjs(a.startDate).diff(dayjs(a.endDate)) - dayjs(b.startDate).diff(dayjs(b.endDate))
    ) ?? ([] as Array<Qualification>);
  const remainingFacts = expertProfile?.expertFacts.slice(1) ?? ([] as Array<FactOnExpert>);
  const pillList = [
    ...(expertProfile?.expertFacts?.[0] ? [expertProfile?.expertFacts?.[0]] : []),
    ...sortedQualifications,
    ...remainingFacts
  ].slice(0, fullscreen ? 6 : 2) as Array<{ title?: string; icon?: string; response?: string }>;
  const avatarVariants = {
    fullscreenContainer: {
      left: 20,
      top: '152px',
      width: '96px',
      height: '96px',
      transition: {
        duration: 0.1,
        ease: 'easeInOut'
      }
    },
    normal: {
      left: 12,
      top: '76px',
      width: '72px',
      height: '72px',
      transition: {
        ease: 'easeInOut',
        duration: 0.1
      }
    }
  };
  const coverPhotoVariants = {
    fullscreenContainer: {
      height: '200px',
      transition: {
        duration: 0.1,
        ease: 'easeInOut'
      }
    },
    normal: {
      height: '112px',
      transition: {
        ease: 'easeInOut',
        duration: 0.1
      }
    }
  };
  const containerVariants = {
    fullscreenContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      transition: {
        duration: 0.1,
        ease: 'easeInOut'
      },
      background: 'white',
      borderRadius: '8px'
    },
    normal: {
      position: 'relative',
      transition: {
        ease: 'easeInOut',
        duration: 0.1
      }
    }
  };
  const pillVariants = {
    fullscreenContainer: {
      opacity: 1,
      transition: {
        duration: 0.2,
        delay: 0.2
      }
    },
    normal: {}
  };
  const contentVariants = {
    fullscreenContainer: {
      marginTop: '252px',
      transition: {
        duration: 0.1
      }
    },
    normal: { marginTop: '160px', transition: { duration: 0.1 } }
  };
  const controls = useAnimation();
  return (
    <Flex>
      <MotionBox
        pos="relative"
        w="100%"
        ref={expertProfileRef}
        variants={containerVariants as Variants}
        animate={controls}
        zIndex="overlay"
        role="row"
      >
        {expertProfile?.coverPhoto && (
          <MotionBox
            animate={controls}
            position="absolute"
            width="100%"
            initial={{ height: 112 }}
            variants={coverPhotoVariants}
          >
            <SkeletonImage
              src={tryToBuildImgixUrl(expertProfile.coverPhoto, { w: 400, h: 300, auto: 'compress,format' })}
              imageProps={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                role: 'presentation',
                onClick: () => {
                  if (!fullscreen) {
                    setFullscreen(true);
                    controls.start('fullscreenContainer');
                  }
                },
                cursor: fullscreen ? 'auto' : 'pointer'
              }}
            />
          </MotionBox>
        )}
        <AnimatePresence>
          {fullscreen && (
            <Button
              pos="absolute"
              top="12px"
              left="12px"
              variant="back-button"
              leftIcon={<LeftChevron fill="scheme.contentInverseSecondary" w="6px" />}
              onClick={() => {
                posthog.capture('CLOSE-EXPERT-PROFILE', {
                  expert_id: expertProfile?.id,
                  expert_name: expertProfile?.publicName
                });
                setFullscreen(false);
                controls.start('normal');
              }}
            >
              {labels.backToConversation}
            </Button>
          )}
        </AnimatePresence>
        {expertProfile?.avatar && (
          <MotionBox
            animate={controls}
            position="absolute"
            initial={{ width: '72px', height: '72px', top: '76px', left: '12px' }}
            variants={avatarVariants}
          >
            <SkeletonImage
              src={tryToBuildImgixUrl(expertProfile.avatar, {
                h: 200,
                w: 200,
                auto: 'format,compress',
                crop: 'faces',
                fit: 'crop'
              })}
              imageProps={{
                objectFit: 'cover',
                borderRadius: '50%',
                border: '2px solid white',
                height: '100%',
                width: '100%',
                role: 'presentation',
                onClick: () => {
                  if (!fullscreen) {
                    setFullscreen(true);
                    controls.start('fullscreenContainer');
                  }
                },
                cursor: fullscreen ? 'auto' : 'pointer'
              }}
            />
          </MotionBox>
        )}
        <MotionBox
          animate={controls}
          variants={contentVariants}
          initial={{ marginTop: '160px' }}
          padding={fullscreen ? '8px' : 0}
          role="gridcell"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              posthog.capture('OPEN-EXPERT-PROFILE', {
                expert_id: expertProfile?.id,
                expert_name: expertProfile?.publicName
              });
              setFullscreen(true);
              controls.start('fullscreenContainer');
            } else if (e.key === 'Escape') {
              posthog.capture('CLOSE-EXPERT-PROFILE', {
                expert_id: expertProfile?.id,
                expert_name: expertProfile?.publicName
              });
              setFullscreen(false);
              controls.start('normal');
            }
          }}
        >
          <Flex justify="space-between" align="center">
            <Flex align="center">
              {expertProfile?.publicName && (
                <Text variant="heading-medium" color="scheme.contentPrimary" mr="8px">
                  {expertProfile?.publicName}
                </Text>
              )}
              {expertProfile?.socialLinks
                ?.filter((social) => social.link)
                ?.slice(0, MAX_SOCIAL_MEDIA_LINKS)
                .map((social) => {
                  if (SOCIAL_MEDIA_ICONS[social.socialMediaNetworkName as keyof typeof SOCIAL_MEDIA_ICONS]) {
                    return (
                      <Box
                        onClick={() => {
                          posthog.capture('CLICK-EXPERT-SOCIAL', {
                            expert_id: expertProfile?.id,
                            expert_name: expertProfile?.publicName,
                            social_name: social.socialMediaNetworkName,
                            social_link: social.link
                          });
                          if (social.link) window.open(social.link);
                        }}
                        key={social.socialMediaNetworkName}
                      >
                        {SOCIAL_MEDIA_ICONS[social.socialMediaNetworkName as keyof typeof SOCIAL_MEDIA_ICONS]}
                      </Box>
                    );
                  }
                })}
              {/* TODO add personal link back in */}
            </Flex>
            {!fullscreen && (
              <Text
                _hover={{ textDecoration: 'underline' }}
                cursor="pointer"
                variant="label-small"
                color="scheme.contentTertiary"
                onClick={() => {
                  posthog.capture('VIEW-EXPERT-PROFILE', {
                    expert_id: expertProfile?.id,
                    expert_name: expertProfile?.publicName
                  });
                  setFullscreen(true);
                  controls.start('fullscreenContainer');
                }}
              >
                {labels.viewExpertProfile}
              </Text>
            )}
          </Flex>
          <Text mt="12px" mb="4px" variant="paragraph-small" color="scheme.contentSecondary">
            {expertProfile?.bio}
          </Text>
          <Flex flexWrap="wrap">
            <AnimatePresence>
              {pillList.map((pill, index) => (
                <MotionBox
                  as="li"
                  listStyleType="none"
                  initial={{ opacity: index < 2 ? 1 : 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.1, delay: index * 0.1 + 0.1 } }}
                  exit={{ opacity: 0 }}
                  key={`${pill.icon}-${index}`}
                  aria-live={index > 2 ? 'polite' : 'off'}
                >
                  <QualificationFactPill
                    icon={pill?.icon ?? ''}
                    text={pill?.response ? pill.response : pill.title ?? ''}
                    key={index}
                  />
                </MotionBox>
              ))}
            </AnimatePresence>
          </Flex>
          <Divider borderColor="scheme.backgroundSecondary" my="20px" />
        </MotionBox>
      </MotionBox>
      {fullscreen && (
        <Box
          h={expertProfileRef.current?.offsetHeight ? expertProfileRef.current?.offsetHeight + 15 : '335px'}
          w="100%"
        />
      )}
    </Flex>
  );
};
